import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  IconButton,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import subscriptionApi from "../../api/subscriptions";
import { format, isValid, parseISO } from "date-fns";
import Layout from "../../components/layout/Layout";

interface Subscription {
  _id: string;
  uid: string;
  userId: {
    _id: string;
    name: string;
    email: string;
    phone: string;
  };
  planId: {
    _id: string;
    title: string;
    description: string;
    price: number;
  };
  status: "active" | "paused" | "cancelled" | "pending" | "completed";
  startDate: string;
  endDate: string;
  slot: string;
  programLength: string;
  progress: number;
  ordersRemaining: number;
  skipsRemaining: number;
  dietaryPreferences: string;
  allergens: string;
  deliveryDetails: {
    name: string;
    company: string;
    flatNumber: string;
    address: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
    postcode: string;
    city: string;
    state: string;
    deliveryInstructions: string[];
    additionalNote: string;
    addressType: string;
    phoneNumber: string;
  };
  total: number;
  totalDiscount: number;
  netAmount: number;
  discountCode: string;
  deliveryTime: string;
  peopleCount: number;
  createdAt: string;
  updatedAt: string;
}

const EditSubscription: React.FC = () => {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    status: "",
    startDate: null as Date | null,
    endDate: null as Date | null,
    slot: "",
    programLength: "",
    progress: 0,
    ordersRemaining: 0,
    skipsRemaining: 0,
    dietaryPreferences: "",
    allergens: "",
    deliveryTime: "",
    peopleCount: 1,
    discountCode: "",
    deliveryDetails: {
      name: "",
      company: "",
      flatNumber: "",
      address: "",
      postcode: "",
      city: "",
      state: "",
      phoneNumber: "",
      additionalNote: "",
      addressType: "",
    },
  });

  useEffect(() => {
    if (subscriptionId) {
      fetchSubscriptionDetails();
    }
  }, [subscriptionId]);

  const fetchSubscriptionDetails = async () => {
    setLoading(true);
    try {
      const data = await subscriptionApi.getSubscriptionDetails(subscriptionId!);
      const sub = data.subscription;
      setSubscription(sub);
      
      // Initialize form data
      setFormData({
        status: sub.status,
        startDate: sub.startDate ? parseISO(sub.startDate) : null,
        endDate: sub.endDate ? parseISO(sub.endDate) : null,
        slot: sub.slot || "",
        programLength: sub.programLength || "",
        progress: sub.progress || 0,
        ordersRemaining: sub.ordersRemaining || 0,
        skipsRemaining: sub.skipsRemaining || 0,
        dietaryPreferences: sub.dietaryPreferences || "",
        allergens: sub.allergens || "",
        deliveryTime: sub.deliveryTime || "",
        peopleCount: sub.peopleCount || 1,
        discountCode: sub.discountCode || "",
        deliveryDetails: {
          name: sub.deliveryDetails?.name || "",
          company: sub.deliveryDetails?.company || "",
          flatNumber: sub.deliveryDetails?.flatNumber || "",
          address: sub.deliveryDetails?.address || "",
          postcode: sub.deliveryDetails?.postcode || "",
          city: sub.deliveryDetails?.city || "",
          state: sub.deliveryDetails?.state || "",
          phoneNumber: sub.deliveryDetails?.phoneNumber || "",
          additionalNote: sub.deliveryDetails?.additionalNote || "",
          addressType: sub.deliveryDetails?.addressType || "",
        },
      });
    } catch (err: any) {
      setError(err.response?.data?.message || "Error fetching subscription details");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDeliveryDetailsChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      deliveryDetails: {
        ...prev.deliveryDetails,
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updateData = {
        ...formData,
        startDate: formData.startDate?.toISOString(),
        endDate: formData.endDate?.toISOString(),
      };

      await subscriptionApi.updateSubscription(subscriptionId!, updateData);
      setSuccess("Subscription updated successfully!");
      
      // Redirect to detail page after a short delay
      setTimeout(() => {
        navigate(`/subscriptions/${subscriptionId}`);
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || "Error updating subscription");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
          <Typography>Loading subscription details...</Typography>
        </Box>
      </Layout>
    );
  }

  if (error && !subscription) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            onClick={() => navigate("/subscriptions")}
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            Back to Subscriptions
          </Button>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={() => navigate(`/subscriptions/${subscriptionId}`)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4">
              Edit Subscription {subscription?.uid}
            </Typography>
          </Box>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 3 }}>
              {/* Basic Information */}
              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Box>

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => handleInputChange("status", e.target.value)}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="paused">Paused</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Program Length"
                value={formData.programLength}
                onChange={(e) => handleInputChange("programLength", e.target.value)}
              />

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={formData.startDate}
                  onChange={(date) => handleInputChange("startDate", date)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={formData.endDate}
                  onChange={(date) => handleInputChange("endDate", date)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>

              <TextField
                fullWidth
                label="Slot"
                value={formData.slot}
                onChange={(e) => handleInputChange("slot", e.target.value)}
              />

              <TextField
                fullWidth
                label="Delivery Time"
                value={formData.deliveryTime}
                onChange={(e) => handleInputChange("deliveryTime", e.target.value)}
              />

              {/* Progress Information */}
              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Progress Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Box>

              <TextField
                fullWidth
                type="number"
                label="Progress"
                value={formData.progress}
                onChange={(e) => handleInputChange("progress", parseInt(e.target.value) || 0)}
                inputProps={{ min: 0 }}
              />

              <TextField
                fullWidth
                type="number"
                label="Orders Remaining"
                value={formData.ordersRemaining}
                onChange={(e) => handleInputChange("ordersRemaining", parseInt(e.target.value) || 0)}
                inputProps={{ min: 0 }}
              />

              <TextField
                fullWidth
                type="number"
                label="Skips Remaining"
                value={formData.skipsRemaining}
                onChange={(e) => handleInputChange("skipsRemaining", parseInt(e.target.value) || 0)}
                inputProps={{ min: 0 }}
              />

              {/* Dietary Information */}
              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Dietary Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Box>

              <FormControl fullWidth>
                <InputLabel>Dietary Preferences</InputLabel>
                <Select
                  value={formData.dietaryPreferences}
                  onChange={(e) => handleInputChange("dietaryPreferences", e.target.value)}
                  label="Dietary Preferences"
                >
                  <MenuItem value="No Preference">No Preference</MenuItem>
                  <MenuItem value="everything">Everything</MenuItem>
                  <MenuItem value="Vegan">Vegan</MenuItem>
                  <MenuItem value="Vegetarian">Vegetarian</MenuItem>
                  <MenuItem value="Keto">Keto</MenuItem>
                  <MenuItem value="gluten-free">Gluten Free</MenuItem>
                  <MenuItem value="dairy-free">Dairy Free</MenuItem>
                  <MenuItem value="paleo">Paleo</MenuItem>
                  <MenuItem value="mediterranean">Mediterranean</MenuItem>
                  <MenuItem value="low-carb">Low Carb</MenuItem>
                  <MenuItem value="high-protein">High Protein</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Allergens</InputLabel>
                <Select
                  value={formData.allergens}
                  onChange={(e) => handleInputChange("allergens", e.target.value)}
                  label="Allergens"
                >
                  <MenuItem value="No Allergies">No Allergies</MenuItem>
                  <MenuItem value="Peanuts">Peanuts</MenuItem>
                  <MenuItem value="Dairy">Dairy</MenuItem>
                  <MenuItem value="Gluten">Gluten</MenuItem>
                  <MenuItem value="Soy">Soy</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>

              {/* Delivery Information */}
              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Delivery Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Box>

              <TextField
                fullWidth
                label="Delivery Name"
                value={formData.deliveryDetails.name}
                onChange={(e) => handleDeliveryDetailsChange("name", e.target.value)}
              />

              <TextField
                fullWidth
                label="Delivery Phone"
                value={formData.deliveryDetails.phoneNumber}
                onChange={(e) => handleDeliveryDetailsChange("phoneNumber", e.target.value)}
              />

              <TextField
                fullWidth
                label="Company"
                value={formData.deliveryDetails.company}
                onChange={(e) => handleDeliveryDetailsChange("company", e.target.value)}
              />

              <TextField
                fullWidth
                label="Flat/Unit Number"
                value={formData.deliveryDetails.flatNumber}
                onChange={(e) => handleDeliveryDetailsChange("flatNumber", e.target.value)}
              />

              <TextField
                fullWidth
                label="Address"
                value={formData.deliveryDetails.address}
                onChange={(e) => handleDeliveryDetailsChange("address", e.target.value)}
                multiline
                rows={2}
                sx={{ gridColumn: "1 / -1" }}
              />

              <TextField
                fullWidth
                label="City"
                value={formData.deliveryDetails.city}
                onChange={(e) => handleDeliveryDetailsChange("city", e.target.value)}
              />

              <TextField
                fullWidth
                label="State"
                value={formData.deliveryDetails.state}
                onChange={(e) => handleDeliveryDetailsChange("state", e.target.value)}
              />

              <TextField
                fullWidth
                label="Postcode"
                value={formData.deliveryDetails.postcode}
                onChange={(e) => handleDeliveryDetailsChange("postcode", e.target.value)}
              />

              <TextField
                fullWidth
                label="Address Type"
                value={formData.deliveryDetails.addressType}
                onChange={(e) => handleDeliveryDetailsChange("addressType", e.target.value)}
              />

              <TextField
                fullWidth
                type="number"
                label="People Count"
                value={formData.peopleCount}
                onChange={(e) => handleInputChange("peopleCount", parseInt(e.target.value) || 1)}
                inputProps={{ min: 1 }}
              />

              <TextField
                fullWidth
                label="Additional Notes"
                value={formData.deliveryDetails.additionalNote}
                onChange={(e) => handleDeliveryDetailsChange("additionalNote", e.target.value)}
                multiline
                rows={3}
                sx={{ gridColumn: "1 / -1" }}
              />

              {/* Other Information */}
              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Other Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Box>

              <TextField
                fullWidth
                label="Discount Code"
                value={formData.discountCode}
                onChange={(e) => handleInputChange("discountCode", e.target.value)}
              />

              {/* Action Buttons */}
              <Box sx={{ gridColumn: "1 / -1", display: "flex", gap: 2, justifyContent: "flex-end", mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/subscriptions/${subscriptionId}`)}
                  startIcon={<CancelIcon />}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  startIcon={<SaveIcon />}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Box>
    </Layout>
  );
};

export default EditSubscription; 